/*
 * Copyright 2023-2025 Steve Xiao (stevexmh@qq.com) and contributors.
 *
 * 本源代码文件是属于 AMLL TTML Tool 项目的一部分。
 * This source code file is a part of AMLL TTML Tool project.
 * 本项目的源代码的使用受到 GNU GENERAL PUBLIC LICENSE version 3 许可证的约束，具体可以参阅以下链接。
 * Use of this source code is governed by the GNU GPLv3 license that can be found through the following link.
 *
 * https://github.com/Steve-xmh/amll-ttml-tool/blob/main/LICENSE
 */

/**
 * @fileoverview
 * 用于将内部歌词数组对象导出成 TTML 格式的模块
 * 但是可能会有信息会丢失
 */

import type {
	LyricLine,
	LyricWord,
	TTMLLangData,
	TTMLLyric,
	TTMLRomanWord,
	TTMLTranslationWord,
} from "../../../types/ttml.ts";
import { log } from "../../../utils/logging.ts";
import { msToTimestamp } from "../../../utils/timestamp.ts";

type LineMetadata = {
	main: string;
	bg: string;
	isAutoFilled?: boolean;
	// 多背景行翻译映射：itunesKey -> 翻译文本
	bgByKey?: Map<string, string>;
};

/**
 * 辅助函数：从 TTMLLangData 或旧格式的字符串/数组中提取数据
 * 兼容旧数据格式：字符串或数组直接作为数据，isAutoFilled 默认为 false
 */
function getLangData<T>(
	value: TTMLLangData<T> | T | undefined,
	defaultValue: T,
): { data: T; isAutoFilled: boolean } {
	if (value === undefined) {
		return { data: defaultValue, isAutoFilled: false };
	}
	// 如果是 TTMLLangData 对象
	if (
		typeof value === "object" &&
		value !== null &&
		"data" in value &&
		!Array.isArray(value)
	) {
		return {
			data: value.data ?? defaultValue,
			isAutoFilled: value.isAutoFilled ?? false,
		};
	}
	// 兼容旧数据：直接是字符串或数组
	return { data: value as T, isAutoFilled: false };
}

export default function exportTTMLText(
	ttmlLyric: TTMLLyric,
	pretty = false,
): string {
	const params: LyricLine[][] = [];
	const lyric = ttmlLyric.lyricLines;

	let tmp: LyricLine[] = [];
	for (const line of lyric) {
		// 当遇到空行时，结束当前 div
		if (line.words.length === 0 && tmp.length > 0) {
			params.push(tmp);
			tmp = [];
			continue;
		}

		if (line.words.length > 0) {
			// 只在以下情况创建新 div：
			// 1. 当前没有 div（tmp.length === 0）
			// 2. 当前行有 songPart（表示新的开始）
			const shouldStartNewDiv = tmp.length === 0 || line.songPart;

			if (shouldStartNewDiv && tmp.length > 0) {
				params.push(tmp);
				tmp = [];
			}

			tmp.push(line);
		}
	}

	if (tmp.length > 0) {
		params.push(tmp);
	}

	// 歌词语言代码：优先使用解析时读取的语言代码，否则使用默认值
	const lyricLang = ttmlLyric.lyricLang ?? "zh-Hans";

	const doc = new Document();

	function createRubyWordElement(word: LyricWord): Element {
		const container = doc.createElement("span");
		container.setAttribute("tts:ruby", "container");
		if (word.obscene) container.setAttribute("amll:obscene", "true");
		if (word.emptyBeat)
			container.setAttribute("amll:empty-beat", `${word.emptyBeat}`);
		if (word.rubyPhraseStart)
			container.setAttribute("amll:rubyPhraseStart", "true");
		const base = doc.createElement("span");
		base.setAttribute("tts:ruby", "base");
		base.appendChild(doc.createTextNode(word.word));
		container.appendChild(base);
		const textContainer = doc.createElement("span");
		textContainer.setAttribute("tts:ruby", "textContainer");
		for (const rubyWord of word.ruby ?? []) {
			const rubySpan = doc.createElement("span");
			rubySpan.setAttribute("tts:ruby", "text");
			rubySpan.setAttribute("begin", msToTimestamp(rubyWord.startTime));
			rubySpan.setAttribute("end", msToTimestamp(rubyWord.endTime));
			rubySpan.appendChild(doc.createTextNode(rubyWord.word));
			textContainer.appendChild(rubySpan);
		}
		container.appendChild(textContainer);
		return container;
	}

	function hasRuby(word: LyricWord): boolean {
		return Array.isArray(word.ruby) && word.ruby.length > 0;
	}

	function createWordElement(word: LyricWord): Element {
		if (Array.isArray(word.ruby) && word.ruby.length > 0) {
			return createRubyWordElement(word);
		}
		const span = doc.createElement("span");
		span.setAttribute("begin", msToTimestamp(word.startTime));
		span.setAttribute("end", msToTimestamp(word.endTime));
		if (word.obscene) span.setAttribute("amll:obscene", "true");
		if (word.emptyBeat)
			span.setAttribute("amll:empty-beat", `${word.emptyBeat}`);
		span.appendChild(doc.createTextNode(word.word));
		return span;
	}

	function findFirstTextNode(node: Node): Text | null {
		if (node.nodeType === Node.TEXT_NODE) return node as Text;
		for (const child of Array.from(node.childNodes)) {
			const found = findFirstTextNode(child);
			if (found) return found;
		}
		return null;
	}

	function findLastTextNode(node: Node): Text | null {
		if (node.nodeType === Node.TEXT_NODE) return node as Text;
		const children = Array.from(node.childNodes);
		for (let i = children.length - 1; i >= 0; i--) {
			const found = findLastTextNode(children[i]);
			if (found) return found;
		}
		return null;
	}

	function addWrapperToElement(el: Element, prefix: string, suffix: string) {
		if (!prefix && !suffix) return;
		const first = findFirstTextNode(el);
		const last = findLastTextNode(el);
		if (!first) return;
		if (first === last) {
			first.nodeValue = `${prefix}${first.nodeValue ?? ""}${suffix}`;
			return;
		}
		if (prefix) {
			first.nodeValue = `${prefix}${first.nodeValue ?? ""}`;
		}
		if (last && suffix) {
			last.nodeValue = `${last.nodeValue ?? ""}${suffix}`;
		}
	}

	function createRomanizationSpanFromData(word: TTMLRomanWord): Element {
		const span = doc.createElement("span");
		span.setAttribute("xmlns", "http://www.w3.org/ns/ttml");
		span.setAttribute("begin", msToTimestamp(word.startTime));
		span.setAttribute("end", msToTimestamp(word.endTime));
		// 去除首尾空格，空格会作为单独的空格文本节点添加
		span.appendChild(doc.createTextNode(word.text.trim()));
		return span;
	}

	// 辅助函数：创建逐字翻译的 span 元素
	function createTranslationSpanFromData(word: TTMLTranslationWord): Element {
		const span = doc.createElement("span");
		span.setAttribute("xmlns", "http://www.w3.org/ns/ttml");
		span.setAttribute("begin", msToTimestamp(word.startTime));
		span.setAttribute("end", msToTimestamp(word.endTime));
		// 去除首尾空格，空格会作为单独的空格文本节点添加
		span.appendChild(doc.createTextNode(word.text.trim()));
		return span;
	}

	function normalizeVocalValue(vocal?: string | string[] | null): string {
		if (!vocal) return "";
		const parts = Array.isArray(vocal) ? vocal : vocal.split(/[\s,]+/);
		return parts
			.map((v) => v.trim())
			.filter(Boolean)
			.join(",");
	}

	const ttRoot = doc.createElement("tt");

	ttRoot.setAttribute("xmlns", "http://www.w3.org/ns/ttml");
	ttRoot.setAttribute("xmlns:ttm", "http://www.w3.org/ns/ttml#metadata");
	ttRoot.setAttribute("xmlns:tts", "http://www.w3.org/ns/ttml#styling");
	ttRoot.setAttribute("xmlns:amll", "http://www.example.com/ns/amll");
	ttRoot.setAttribute(
		"xmlns:itunes",
		"http://music.apple.com/lyric-ttml-internal",
	);
	// 设置歌词语言代码
	ttRoot.setAttribute("xml:lang", lyricLang);

	// Determine itunes:timing mode for Spicylyrics compatibility
	// Word = at least one line has 2+ non-blank words (dynamic/per-word timing)
	// Line = has lyric lines but every line has 0 or 1 non-blank word
	// None = no timed words at all
	const nonBlankWordCountsPerLine = lyric.map(
		(l) => l.words.filter((w) => w.word.trim().length > 0).length,
	);
	const totalNonBlankWords = nonBlankWordCountsPerLine.reduce(
		(sum, v) => sum + v,
		0,
	);
	const hasAnyTiming = lyric.some((l) =>
		l.words.some((w) => w.word.trim().length > 0 && w.endTime > w.startTime),
	);
	let timingMode: "Word" | "Line" | "None";
	if (totalNonBlankWords === 0 || !hasAnyTiming) timingMode = "None";
	else if (nonBlankWordCountsPerLine.some((c) => c > 1)) timingMode = "Word";
	else timingMode = "Line";
	ttRoot.setAttribute("itunes:timing", timingMode);

	doc.appendChild(ttRoot);

	const head = doc.createElement("head");

	ttRoot.appendChild(head);

	const body = doc.createElement("body");
	const hasOtherPerson = !!lyric.find((v) => v.isDuet);

	const metadataEl = doc.createElement("metadata");

	// 导出 agents 数组
	const agents = ttmlLyric.agents ?? [];
	if (agents.length > 0) {
		// 使用已有的 agents
		for (const agent of agents) {
			const agentEl = doc.createElement("ttm:agent");
			agentEl.setAttribute("type", agent.type);
			agentEl.setAttribute("xml:id", agent.id);

			// 添加 ttm:name 子元素
			for (const name of agent.names) {
				const nameEl = doc.createElement("ttm:name");
				nameEl.setAttribute("type", "full");
				nameEl.appendChild(doc.createTextNode(name));
				agentEl.appendChild(nameEl);
			}

			metadataEl.appendChild(agentEl);
		}
	} else {
		// agents 为空时添加默认 agent
		const mainPersonAgent = doc.createElement("ttm:agent");
		mainPersonAgent.setAttribute("type", "person");
		mainPersonAgent.setAttribute("xml:id", "v1");
		metadataEl.appendChild(mainPersonAgent);

		if (hasOtherPerson) {
			const otherPersonAgent = doc.createElement("ttm:agent");
			otherPersonAgent.setAttribute("type", "other");
			otherPersonAgent.setAttribute("xml:id", "v2");
			metadataEl.appendChild(otherPersonAgent);
		}
	}

	const vocalTags =
		ttmlLyric.vocalTags?.filter(
			(tag) => tag.key && tag.key.trim().length > 0,
		) ?? [];
	if (vocalTags.length > 0) {
		const vocalsEl = doc.createElement("amll:vocals");
		for (const tag of vocalTags) {
			const vocalEl = doc.createElement("vocal");
			vocalEl.setAttribute("key", tag.key);
			vocalEl.setAttribute("value", tag.value ?? "");
			vocalsEl.appendChild(vocalEl);
		}
		metadataEl.appendChild(vocalsEl);
	}

	// Append metadata entries (songwriter will be handled in iTunesMetadata later)
	for (const metadata of ttmlLyric.metadata) {
		// songwriter 会在 iTunesMetadata 中单独处理，不在此处重复导出
		if (metadata.key === "songwriter") continue;
		for (const value of metadata.value) {
			const metaEl = doc.createElement("amll:meta");
			metaEl.setAttribute("key", metadata.key);
			metaEl.setAttribute("value", value);
			metadataEl.appendChild(metaEl);
		}
	}

	head.appendChild(metadataEl);

	// L 从 0 开始 (L0, L1, L2...)，B 从 1 开始 (B1, B2, B3...)
	let i = 0;
	let bCounter = 1;

	const translationByLangMap = new Map<string, Map<string, LineMetadata>>();
	const wordTranslationByLangMap = new Map<
		string,
		Map<
			string,
			{
				mainWords: LyricWord[];
				bgWords: LyricWord[];
				mainTrans: TTMLTranslationWord[];
				bgTrans: TTMLTranslationWord[];
				isAutoFilled?: boolean;
			}
		>
	>();
	const romanizationByLangMap = new Map<string, Map<string, LineMetadata>>();
	const wordRomanizationByLangMap = new Map<
		string,
		Map<
			string,
			{
				mainWords: LyricWord[];
				bgWords: LyricWord[];
				mainRoman: TTMLRomanWord[];
				bgRoman: TTMLRomanWord[];
				isAutoFilled?: boolean;
			}
		>
	>();

	const guessDuration = lyric[lyric.length - 1]?.endTime ?? 0;
	body.setAttribute("dur", msToTimestamp(guessDuration));
	const isDynamicLyric = lyric.some(
		(line) => line.words.filter((v) => v.word.trim().length > 0).length > 1,
	);

	for (const param of params) {
		const paramDiv = doc.createElement("div");
		const beginTime = param[0]?.startTime ?? 0;
		const endTime = param[param.length - 1]?.endTime ?? 0;

		paramDiv.setAttribute("begin", msToTimestamp(beginTime));
		paramDiv.setAttribute("end", msToTimestamp(endTime));

		// 查找该 div 中第一个有 songPart 的非背景行，将其 songPart 写入 div
		const firstLineWithSongPart = param.find(
			(line) => line.songPart && line.songPart.trim().length > 0 && !line.isBG,
		);
		if (firstLineWithSongPart?.songPart) {
			paramDiv.setAttribute("itunes:song-part", firstLineWithSongPart.songPart);
		}

		for (let lineIndex = 0; lineIndex < param.length; lineIndex++) {
			const line = param[lineIndex];
			// 跳过背景行，它们会被作为子元素嵌套在主行中
			if (line.isBG) continue;
			const lineP = doc.createElement("p");
			const beginTime = line.startTime ?? 0;
			const endTime = line.endTime;

			lineP.setAttribute("begin", msToTimestamp(beginTime));
			lineP.setAttribute("end", msToTimestamp(endTime));

			// 优先使用 line.agent，如果没有则根据 isDuet 判断
			const agentId = line.agent ?? (line.isDuet ? "v2" : "v1");
			lineP.setAttribute("ttm:agent", agentId);
			const normalizedVocal = normalizeVocalValue(line.vocal);
			if (normalizedVocal.length > 0) {
				lineP.setAttribute("amll:vocal", normalizedVocal);
			}

			// 写入 RTL 标记
			if (line.isRtl) {
				lineP.setAttribute("amll:rtl", "true");
			}

			// 分配或复用 itunesKey
			// 主行使用 L 编号，背景行使用 B 编号
			let itunesKey: string;
			if (line.itunesKey?.match(/^L\d+$/)) {
				// 复用现有的 L 编号
				itunesKey = line.itunesKey;
				// 更新计数器
				const keyNum = Number.parseInt(itunesKey.slice(1));
				if (keyNum >= i) i = keyNum + 1;
			} else {
				// 分配新的 L 编号
				itunesKey = `L${i}`;
				i++;
			}
			lineP.setAttribute("itunes:key", itunesKey);

			const mainWords = line.words;
			const bgLines: LyricLine[] = [];
			const bgWordsList: LyricWord[][] = [];

			if (isDynamicLyric) {
				let beginTime = Number.POSITIVE_INFINITY;
				let endTime = 0;
				for (const word of line.words) {
					if (word.word.trim().length === 0 && !hasRuby(word)) {
						lineP.appendChild(doc.createTextNode(word.word));
					} else {
						const span = createWordElement(word);
						lineP.appendChild(span);
						beginTime = Math.min(beginTime, word.startTime);
						endTime = Math.max(endTime, word.endTime);
					}
				}
				lineP.setAttribute("begin", msToTimestamp(line.startTime));
				lineP.setAttribute("end", msToTimestamp(line.endTime));
			} else {
				const word = line.words[0];
				if (word.word.trim().length === 0 && !hasRuby(word)) {
					lineP.appendChild(doc.createTextNode(word.word));
				} else {
					lineP.appendChild(createWordElement(word));
				}
				lineP.setAttribute("begin", msToTimestamp(word.startTime));
				lineP.setAttribute("end", msToTimestamp(word.endTime));
			}

			// 处理所有连续的背景行（多背景行支持）
			while (lineIndex + 1 < param.length && param[lineIndex + 1]?.isBG) {
				lineIndex++;
				const bgLine = param[lineIndex];
				bgLines.push(bgLine);
				bgWordsList.push(bgLine.words);

				const bgLineSpan = doc.createElement("span");
				bgLineSpan.setAttribute("xmlns", "http://www.w3.org/ns/ttml");
				bgLineSpan.setAttribute(
					"xmlns:ttm",
					"http://www.w3.org/ns/ttml#metadata",
				);
				bgLineSpan.setAttribute("ttm:role", "x-bg");

				// 为 bg 行分配或复用 itunesKey（B 编号）
				let bgItunesKey: string;
				if (bgLine.itunesKey?.match(/^B\d+$/)) {
					// 复用现有的 B 编号
					bgItunesKey = bgLine.itunesKey;
					// 更新 B 计数器
					const keyNum = Number.parseInt(bgItunesKey.slice(1));
					if (keyNum >= bCounter) bCounter = keyNum + 1;
				} else {
					// 分配新的 B 编号
					bgItunesKey = `B${bCounter}`;
					bCounter++;
				}
				bgLineSpan.setAttribute("itunes:key", bgItunesKey);

				// 为 bg 行导出 agent 属性（如果有的话）
				if (bgLine.agent) {
					bgLineSpan.setAttribute("ttm:agent", bgLine.agent);
				}

				// 为 bg 行导出 RTL 属性
				if (bgLine.isRtl) {
					bgLineSpan.setAttribute("amll:rtl", "true");
				}

				if (isDynamicLyric) {
					let beginTime = Number.POSITIVE_INFINITY;
					let endTime = 0;

					const firstWordIndex = bgLine.words.findIndex(
						(w) => w.word.trim().length > 0,
					);
					const lastWordIndex = bgLine.words
						.map((w) => w.word.trim().length > 0)
						.lastIndexOf(true);

					for (
						let wordIndex = 0;
						wordIndex < bgLine.words.length;
						wordIndex++
					) {
						const word = bgLine.words[wordIndex];
						if (word.word.trim().length === 0 && !hasRuby(word)) {
							bgLineSpan.appendChild(doc.createTextNode(word.word));
						} else {
							const span = createWordElement(word);

							const prefix = wordIndex === firstWordIndex ? "(" : "";
							const suffix = wordIndex === lastWordIndex ? ")" : "";
							addWrapperToElement(span, prefix, suffix);

							bgLineSpan.appendChild(span);
							beginTime = Math.min(beginTime, word.startTime);
							endTime = Math.max(endTime, word.endTime);
						}
					}
					bgLineSpan.setAttribute("begin", msToTimestamp(beginTime));
					bgLineSpan.setAttribute("end", msToTimestamp(endTime));
				} else {
					const word = bgLine.words[0];
					if (word.word.trim().length === 0 && !hasRuby(word)) {
						bgLineSpan.appendChild(doc.createTextNode(`(${word.word})`));
					} else {
						const span = createWordElement(word);
						addWrapperToElement(span, "(", ")");
						bgLineSpan.appendChild(span);
					}
					bgLineSpan.setAttribute("begin", msToTimestamp(word.startTime));
					bgLineSpan.setAttribute("end", msToTimestamp(word.endTime));
				}

				const normalizedBgVocal = normalizeVocalValue(bgLine.vocal);
				if (normalizedBgVocal.length > 0) {
					bgLineSpan.setAttribute("amll:vocal", normalizedBgVocal);
				}

				lineP.appendChild(bgLineSpan);
			}

			// 收集翻译数据：只输出有语言代码的翻译（translatedLyricByLang）
			const translationLangs = new Set<string>([
				...Object.keys(line.translatedLyricByLang ?? {}),
				...bgLines.flatMap((bg) => Object.keys(bg.translatedLyricByLang ?? {})),
			]);
			// 处理有语言代码的翻译（跳过 und）
		for (const lang of translationLangs) {
			if (lang === "und") continue; // 跳过 und，不输出
			const mainData = line.translatedLyricByLang?.[lang];
			// 收集所有背景行的翻译，按 itunesKey 分组
			const bgByKey = new Map<string, string>();
			for (const bgLine of bgLines) {
				const bgData = bgLine.translatedLyricByLang?.[lang];
				if (bgData && bgLine.itunesKey) {
					const bgParsed = getLangData(bgData, "");
					if (bgParsed.data.trim()) {
						bgByKey.set(bgLine.itunesKey, bgParsed.data);
					}
				}
			}
			// 如果没有多背景行，使用旧的 bg 字段
			let bg = "";
			if (bgLines.length === 1 && bgByKey.size === 0) {
				const bgData = bgLines[0]?.translatedLyricByLang?.[lang];
				if (bgData) {
					bg = getLangData(bgData, "").data;
				}
			}
			// 使用辅助函数兼容旧数据格式
			const mainParsed = getLangData(mainData, "");
			const main = mainParsed.data;
			// 检查是否为自动填充的语言代码
			const isAutoFilled = mainParsed.isAutoFilled;
			if (main.trim().length === 0 && bg.trim().length === 0 && bgByKey.size === 0) continue;
			if (!translationByLangMap.has(lang)) {
				translationByLangMap.set(lang, new Map());
			}
			translationByLangMap.get(lang)?.set(itunesKey, { main, bg, isAutoFilled, bgByKey });
		}
			// 注意：不输出无语言代码的 translatedLyric

			// 2. 然后收集逐字翻译（wordTranslationByLang），会覆盖逐行翻译
			const wordTransLangs = new Set<string>([
				...Object.keys(line.wordTranslationByLang ?? {}),
				...bgLines.flatMap((bg) => Object.keys(bg.wordTranslationByLang ?? {})),
			]);
			// 处理有语言代码的逐字翻译（跳过 und）
		for (const lang of wordTransLangs) {
			if (lang === "und") continue; // 跳过 und，不输出
			const mainData = line.wordTranslationByLang?.[lang];
			const bgData = bgLines.length === 1 ? bgLines[0]?.wordTranslationByLang?.[lang] : undefined;
			// 使用辅助函数兼容旧数据格式
			const mainParsed = getLangData(mainData, []);
			const bgParsed = getLangData(bgData, []);
			const mainTrans = mainParsed.data;
			const bgTrans = bgParsed.data;
			// 检查是否为自动填充的语言代码
			const isAutoFilled = mainParsed.isAutoFilled || bgParsed.isAutoFilled;
			if (mainTrans.length === 0 && bgTrans.length === 0) continue;
			// 逐字翻译优先：删除相同语言的逐行翻译
			translationByLangMap.delete(lang);
			if (!wordTranslationByLangMap.has(lang)) {
				wordTranslationByLangMap.set(lang, new Map());
			}
			wordTranslationByLangMap.get(lang)?.set(itunesKey, {
				mainWords,
				bgWords: bgWordsList[0] ?? [],
				mainTrans,
				bgTrans,
				isAutoFilled,
			});
		}

			// 收集音译数据：逐字音译优先于逐行音译
			// 1. 首先收集逐行音译（romanLyricByLang）
			const romanLangs = new Set<string>([
				...Object.keys(line.romanLyricByLang ?? {}),
				...bgLines.flatMap((bg) => Object.keys(bg.romanLyricByLang ?? {})),
			]);
			// 处理有语言代码的逐行音译（跳过 und）
		for (const lang of romanLangs) {
			if (lang === "und") continue; // 跳过 und，不输出
			const mainData = line.romanLyricByLang?.[lang];
			const bgData = bgLines.length === 1 ? bgLines[0]?.romanLyricByLang?.[lang] : undefined;
			// 使用辅助函数兼容旧数据格式
			const mainParsed = getLangData(mainData, "");
			const bgParsed = getLangData(bgData, "");
			const main = mainParsed.data;
			const bg = bgParsed.data;
			// 检查是否为自动填充的语言代码
			const isAutoFilled = mainParsed.isAutoFilled || bgParsed.isAutoFilled;
			if (main.trim().length === 0 && bg.trim().length === 0) continue;
			if (!romanizationByLangMap.has(lang)) {
				romanizationByLangMap.set(lang, new Map());
			}
			romanizationByLangMap.get(lang)?.set(itunesKey, { main, bg, isAutoFilled });
		}
			// 注意：不输出无语言代码的 romanLyric

			// 2. 然后收集逐字音译（wordRomanizationByLang），会覆盖逐行音译
			const wordRomanLangs = new Set<string>([
				...Object.keys(line.wordRomanizationByLang ?? {}),
				...bgLines.flatMap((bg) => Object.keys(bg.wordRomanizationByLang ?? {})),
			]);
			// 处理有语言代码的逐字音译（跳过 und）
		for (const lang of wordRomanLangs) {
			if (lang === "und") continue; // 跳过 und，不输出
			const mainData = line.wordRomanizationByLang?.[lang];
			const bgData = bgLines.length === 1 ? bgLines[0]?.wordRomanizationByLang?.[lang] : undefined;
			// 使用辅助函数兼容旧数据格式
			const mainParsed = getLangData(mainData, []);
			const bgParsed = getLangData(bgData, []);
			const mainRoman = mainParsed.data;
			const bgRoman = bgParsed.data;
			// 检查是否为自动填充的语言代码
			const isAutoFilled = mainParsed.isAutoFilled || bgParsed.isAutoFilled;
			if (mainRoman.length === 0 && bgRoman.length === 0) continue;
			// 逐字音译优先：删除相同语言的逐行音译
			romanizationByLangMap.delete(lang);
			if (!wordRomanizationByLangMap.has(lang)) {
				wordRomanizationByLangMap.set(lang, new Map());
			}
			wordRomanizationByLangMap.get(lang)?.set(itunesKey, {
				mainWords,
				bgWords: bgWordsList[0] ?? [],
				mainRoman,
				bgRoman,
				isAutoFilled,
			});
		}
			// 注意：不输出无语言代码的 word.romanWord

			paramDiv.appendChild(lineP);
		}

		body.appendChild(paramDiv);
	}

	// 检查是否需要创建 iTunesMetadata（songwriter、translations、transliterations）
	const hasSongwriter = ttmlLyric.metadata.some(
		(m) => m.key === "songwriter" && m.value.some((v) => v.trim().length > 0),
	);
	const hasTranslations =
		translationByLangMap.size > 0 || wordTranslationByLangMap.size > 0;
	const hasTransliterations =
		romanizationByLangMap.size > 0 || wordRomanizationByLangMap.size > 0;

	if (hasSongwriter || hasTranslations || hasTransliterations) {
		const iTunesMetadata = doc.createElement("iTunesMetadata");
		iTunesMetadata.setAttribute(
			"xmlns",
			"http://music.apple.com/lyric-ttml-internal",
		);

		// 1. 添加 songwriter
		if (hasSongwriter) {
			const songwriterMeta = ttmlLyric.metadata.find(
				(m) =>
					m.key === "songwriter" && m.value.some((v) => v.trim().length > 0),
			);
			if (songwriterMeta) {
				const songwritersEl = doc.createElement("songwriters");
				for (const name of songwriterMeta.value) {
					const trimmed = name.trim();
					if (!trimmed) continue;
					const swEl = doc.createElement("songwriter");
					swEl.appendChild(doc.createTextNode(trimmed));
					songwritersEl.appendChild(swEl);
				}
				if (songwritersEl.childNodes.length > 0) {
					iTunesMetadata.appendChild(songwritersEl);
				}
			}
		}

		// 2. 添加 translations
		if (hasTranslations) {
			const translations = doc.createElement("translations");
			// 用于缓存已创建的 translation 元素，避免重复创建
			const translationCache = new Map<string, Element>();

			// 辅助函数：获取或创建 translation 元素
			const getOrCreateTranslation = (lang: string, type: string, isAutoFilled?: boolean): Element => {
				const cacheKey = `${lang}:${type}`;
				const cached = translationCache.get(cacheKey);
				if (cached) {
					return cached;
				}
				const translation = doc.createElement("translation");
				// 如果不是自动填充的语言代码，才设置 xml:lang 属性
				if (!isAutoFilled) {
					translation.setAttribute("xml:lang", lang);
				}
				translation.setAttribute("type", type);
				translations.appendChild(translation);
				translationCache.set(cacheKey, translation);
				return translation;
			};

			// 2.1 处理逐行翻译（translationByLangMap）- type="subtitle"
			for (const [lang, entries] of translationByLangMap.entries()) {
				for (const [key, { main, bg, isAutoFilled, bgByKey }] of entries.entries()) {
					const textEl = doc.createElement("text");
					textEl.setAttribute("for", key);
					if (main.trim().length > 0) {
						textEl.appendChild(doc.createTextNode(main));
					}
					// 多背景行支持：优先输出 bgByKey
					if (bgByKey && bgByKey.size > 0) {
						for (const [bgKey, bgText] of bgByKey.entries()) {
							if (bgText.trim().length === 0) continue;
							const bgSpan = doc.createElement("span");
							bgSpan.setAttribute("xmlns", "http://www.w3.org/ns/ttml");
							bgSpan.setAttribute(
								"xmlns:ttm",
								"http://www.w3.org/ns/ttml#metadata",
							);
							bgSpan.setAttribute("ttm:role", "x-bg");
							bgSpan.setAttribute("for", bgKey);
							bgSpan.appendChild(doc.createTextNode(bgText));
							textEl.appendChild(bgSpan);
						}
					} else if (bg.trim().length > 0) {
						// 旧格式：单个背景行
						const bgSpan = doc.createElement("span");
						bgSpan.setAttribute("xmlns", "http://www.w3.org/ns/ttml");
						bgSpan.setAttribute(
							"xmlns:ttm",
							"http://www.w3.org/ns/ttml#metadata",
						);
						bgSpan.setAttribute("ttm:role", "x-bg");
						bgSpan.appendChild(doc.createTextNode(bg));
						textEl.appendChild(bgSpan);
					}
					const translation = getOrCreateTranslation(lang, "subtitle", isAutoFilled);
					translation.appendChild(textEl);
				}
			}

			// 2.2 处理逐字翻译（wordTranslationByLangMap）- type="replacement"
			for (const [lang, entries] of wordTranslationByLangMap.entries()) {
				for (const [key, data] of entries.entries()) {
					const textEl = doc.createElement("text");
					textEl.setAttribute("for", key);

					if (data.mainTrans.length > 0) {
						for (const word of data.mainWords) {
							if (word.word.trim().length === 0) {
								if (textEl.hasChildNodes()) {
									textEl.appendChild(doc.createTextNode(word.word));
								}
								continue;
							}
							const match = data.mainTrans.find(
								(r) =>
									r.startTime === word.startTime && r.endTime === word.endTime,
							);
							if (!match || match.text.length === 0) continue;
							textEl.appendChild(createTranslationSpanFromData(match));
							// 如果 hasSpaceAfter 为 true，添加空格文本节点
							if (match.hasSpaceAfter) {
								textEl.appendChild(doc.createTextNode(" "));
							}
						}
					}

					if (data.bgTrans.length > 0) {
						const bgSpan = doc.createElement("span");
						bgSpan.setAttribute("xmlns", "http://www.w3.org/ns/ttml");
						bgSpan.setAttribute(
							"xmlns:ttm",
							"http://www.w3.org/ns/ttml#metadata",
						);
						bgSpan.setAttribute("ttm:role", "x-bg");
						const bgSpans: Element[] = [];
						for (const word of data.bgWords) {
							if (word.word.trim().length === 0) {
								if (bgSpan.hasChildNodes()) {
									bgSpan.appendChild(doc.createTextNode(word.word));
								}
								continue;
							}
							const match = data.bgTrans.find(
								(r) =>
									r.startTime === word.startTime && r.endTime === word.endTime,
							);
							if (!match || match.text.length === 0) continue;
							const span = createTranslationSpanFromData(match);
							bgSpan.appendChild(span);
							bgSpans.push(span);
							// 如果 hasSpaceAfter 为 true，添加空格文本节点
							if (match.hasSpaceAfter) {
								bgSpan.appendChild(doc.createTextNode(" "));
							}
						}
						if (bgSpans.length > 0) {
							const first = bgSpans[0];
							const last = bgSpans[bgSpans.length - 1];
							if (first.firstChild) {
								first.firstChild.nodeValue = `(${first.firstChild.nodeValue}`;
							}
							if (last.firstChild) {
								last.firstChild.nodeValue = `${last.firstChild.nodeValue})`;
							}
							textEl.appendChild(bgSpan);
						}
					}
					const translation = getOrCreateTranslation(lang, "replacement", data.isAutoFilled);
					translation.appendChild(textEl);
				}
			}

			iTunesMetadata.appendChild(translations);
		}

		// 3. 添加 transliterations
		if (hasTransliterations) {
			const transliterations = doc.createElement("transliterations");
			// 用于缓存已创建的 transliteration 元素，避免使用 querySelector
			const transliterationCache = new Map<string, Element>();

			// 辅助函数：获取或创建 transliteration 元素
			const getOrCreateTransliteration = (lang: string, isAutoFilled?: boolean): Element => {
				const cached = transliterationCache.get(lang);
				if (cached) {
					return cached;
				}
				const transliteration = doc.createElement("transliteration");
				// 如果不是自动填充的语言代码，才设置 xml:lang 属性
				if (!isAutoFilled) {
					transliteration.setAttribute("xml:lang", lang);
				}
				transliterations.appendChild(transliteration);
				transliterationCache.set(lang, transliteration);
				return transliteration;
			};

			// 处理逐行音译（romanizationByLangMap）
			for (const [lang, entries] of romanizationByLangMap.entries()) {
				for (const [key, { main, bg, isAutoFilled }] of entries.entries()) {
					const textEl = doc.createElement("text");
					textEl.setAttribute("for", key);
					if (main.trim().length > 0) {
						textEl.appendChild(doc.createTextNode(main));
					}
					if (bg.trim().length > 0) {
						const bgSpan = doc.createElement("span");
						bgSpan.setAttribute("xmlns", "http://www.w3.org/ns/ttml");
						bgSpan.setAttribute(
							"xmlns:ttm",
							"http://www.w3.org/ns/ttml#metadata",
						);
						bgSpan.setAttribute("ttm:role", "x-bg");
						bgSpan.appendChild(doc.createTextNode(bg));
						textEl.appendChild(bgSpan);
					}
					const transliteration = getOrCreateTransliteration(lang, isAutoFilled);
					transliteration.appendChild(textEl);
				}
			}

			// 处理逐字音译（wordRomanizationByLangMap）
			for (const [lang, entries] of wordRomanizationByLangMap.entries()) {
				for (const [key, data] of entries.entries()) {
					const textEl = doc.createElement("text");
					textEl.setAttribute("for", key);

					if (data.mainRoman.length > 0) {
						for (const word of data.mainWords) {
							if (word.word.trim().length === 0) {
								if (textEl.hasChildNodes()) {
									textEl.appendChild(doc.createTextNode(word.word));
								}
								continue;
							}
							const match = data.mainRoman.find(
								(r) =>
									r.startTime === word.startTime && r.endTime === word.endTime,
							);
							if (!match || match.text.length === 0) continue;
							textEl.appendChild(createRomanizationSpanFromData(match));
							// 如果 hasSpaceAfter 为 true，添加空格文本节点
							if (match.hasSpaceAfter) {
								textEl.appendChild(doc.createTextNode(" "));
							}
						}
					}

					if (data.bgRoman.length > 0) {
						const bgSpan = doc.createElement("span");
						bgSpan.setAttribute("xmlns", "http://www.w3.org/ns/ttml");
						bgSpan.setAttribute(
							"xmlns:ttm",
							"http://www.w3.org/ns/ttml#metadata",
						);
						bgSpan.setAttribute("ttm:role", "x-bg");
						const bgSpans: Element[] = [];
						for (const word of data.bgWords) {
							if (word.word.trim().length === 0) {
								if (bgSpan.hasChildNodes()) {
									bgSpan.appendChild(doc.createTextNode(word.word));
								}
								continue;
							}
							const match = data.bgRoman.find(
								(r) =>
									r.startTime === word.startTime && r.endTime === word.endTime,
							);
							if (!match || match.text.length === 0) continue;
							const span = createRomanizationSpanFromData(match);
							bgSpan.appendChild(span);
							bgSpans.push(span);
							// 如果 hasSpaceAfter 为 true，添加空格文本节点
							if (match.hasSpaceAfter) {
								bgSpan.appendChild(doc.createTextNode(" "));
							}
						}
						if (bgSpans.length > 0) {
							const first = bgSpans[0];
							const last = bgSpans[bgSpans.length - 1];
							if (first.firstChild) {
								first.firstChild.nodeValue = `(${first.firstChild.nodeValue}`;
							}
							if (last.firstChild) {
								last.firstChild.nodeValue = `${last.firstChild.nodeValue})`;
							}
							textEl.appendChild(bgSpan);
						}
					}

					const transliteration = getOrCreateTransliteration(lang, data.isAutoFilled);
					transliteration.appendChild(textEl);
				}
			}

			iTunesMetadata.appendChild(transliterations);
		}

		// 将 iTunesMetadata 添加到 metadata 的最后
		metadataEl.appendChild(iTunesMetadata);
	}

	ttRoot.appendChild(body);
	log("ttml document built", ttRoot);

	if (pretty) {
		const xsltDoc = new DOMParser().parseFromString(
			[
				'<xsl:stylesheet xmlns:xsl="http://www.w3.org/1999/XSL/Transform" version="1.0">',
				'  <xsl:strip-space elements="*"/>',
				'  <xsl:template match="para[content-style][not(text())]">',
				'    <xsl:value-of select="normalize-space(.)"/>',
				"  </xsl:template>",
				'  <xsl:template match="node()|@*">',
				'    <xsl:copy><xsl:apply-templates select="node()|@*"/></xsl:copy>',
				"  </xsl:template>",
				'  <xsl:output indent="yes"/>',
				"</xsl:stylesheet>",
			].join("\n"),
			"application/xml",
		);

		const xsltProcessor = new XSLTProcessor();
		xsltProcessor.importStylesheet(xsltDoc);
		const resultDoc = xsltProcessor.transformToDocument(doc);

		return new XMLSerializer().serializeToString(resultDoc);
	}
	return new XMLSerializer().serializeToString(doc);
}
